import type React from 'react';
import { createContext, type ReactNode, useContext, useEffect, useRef, useState } from 'react';
import type { ShiftAccountAccess } from 'shared-models';
import { shiftAuthService } from '../../services/auth/shift-auth.service';
import { shiftPushService } from '../../services/pwa/shift-push.service';
import type { ShiftAuthUser, ShiftLoginResponse } from '../../models/auth.types';

type ShiftAuthContextValue = {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: ShiftAuthUser | null;
  login: (payload: ShiftLoginResponse) => void;
  refreshMe: () => Promise<void>;
  logout: () => Promise<void>;
  applyAccountAccess: (access:ShiftAccountAccess) => void;
};

const ShiftAuthContext = createContext<ShiftAuthContextValue | undefined>(undefined);

export const ShiftAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<ShiftAuthUser | null>(null);
  const authGeneration = useRef(0);
  const lastAccess = useRef<{id:number;revision:number} | null>(null);

  const refreshMe = async () => {
    const generation = authGeneration.current;
    try {
      const profile = await shiftAuthService.me();
      if (generation!==authGeneration.current) return;
      setUser(profile);
      setIsAuthenticated(true);
    } catch {
      if (generation!==authGeneration.current) return;
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  useEffect(() => {
    const bootstrap = async () => {
      await refreshMe();
      setIsLoading(false);
    };

    void bootstrap();
  }, []);

  const login = (payload: ShiftLoginResponse) => {
    authGeneration.current += 1;
    lastAccess.current = null;
    setUser(payload.user);
    setIsAuthenticated(true);
  };

  const logout = async () => {
    authGeneration.current += 1;
    await shiftPushService.disable();
    try {
      await shiftAuthService.logout();
    } finally {
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const applyAccountAccess = (access:ShiftAccountAccess) => {
    if (!user || user.id!==access.shiftUserId || (lastAccess.current?.id===user.id && lastAccess.current.revision>=access.revision)) return;
    lastAccess.current = {id:user.id,revision:access.revision};
    authGeneration.current += 1;
    if (access.state!=='active' || !access.employeeIsActive) {
      setUser(null);
      setIsAuthenticated(false);
      return;
    }
    setUser(current => current ? {...current,role:access.role,canEditEmployees:access.canEditEmployees,
      canAssignEmployees:access.canAssignEmployees,canManageSchedule:access.canManageSchedule,canCreateEmployees:access.canCreateEmployees} : current);
  };

  return (
    <ShiftAuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        login,
        refreshMe,
        logout,
        applyAccountAccess,
      }}
    >
      {children}
    </ShiftAuthContext.Provider>
  );
};

export const useShiftAuth = () => {
  const context = useContext(ShiftAuthContext);
  if (!context) {
    throw new Error('useShiftAuth must be used within ShiftAuthProvider');
  }
  return context;
};
